/**
 * Work Log 목록 컴포넌트
 */

import { STATUS_LABELS, WORK_LOG_CATEGORIES, WORK_LOG_STATUSES, WorkLog, WorkLogCategory, WorkLogStatus } from '../../constants/workLog'
import { formatDateTime, formatDuration, getCategoryColor } from '../../utils/workLogHelpers'

/**
 * Tiptap HTML 본문에서 태그·엔티티를 제거한 평문 스니펫을 반환한다.
 * 리스트 프리뷰처럼 한두 줄만 보여주는 곳에서 태그가 노출되지 않도록 사용한다.
 */
function toPlainPreview(html: string | null | undefined): string {
  if (!html) return ''
  const withoutTags = html.replace(/<[^>]*>/g, ' ')
  const decoded = withoutTags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
  return decoded.replace(/\s+/g, ' ').trim()
}

interface WorkLogListProps {
  workLogs: WorkLog[]
  loading: boolean
  selectedStatus: WorkLogStatus | 'all'
  selectedCategory: WorkLogCategory | 'all'
  onStatusChange: (status: WorkLogStatus | 'all') => void
  onCategoryChange: (category: WorkLogCategory | 'all') => void
  onWorkLogClick: (workLog: WorkLog) => void
  onCreateClick: () => void
}

export function WorkLogList({
  workLogs,
  loading,
  selectedStatus,
  selectedCategory,
  onStatusChange,
  onCategoryChange,
  onWorkLogClick,
  onCreateClick
}: WorkLogListProps) {
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">지원 이력</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            고객사별 지원 이력을 기록합니다.
          </p>
        </div>
        <button
          onClick={onCreateClick}
          className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-600 text-white font-medium rounded-lg hover:from-primary-700 hover:to-primary-800 dark:hover:from-primary-600 dark:hover:to-primary-500 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          새 업무 기록
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
        {/* Status Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex overflow-x-auto">
            {WORK_LOG_STATUSES.map((status) => (
              <button
                key={status}
                onClick={() => {
                  onStatusChange(status)
                  onCategoryChange('all')
                }}
                className={`
                  flex-1 min-w-[100px] px-6 py-4 text-sm font-medium transition-all duration-200
                  ${selectedStatus === status
                    ? 'text-primary-600 dark:text-primary-300 border-b-2 border-primary-600 dark:border-primary-400 bg-primary-50 dark:bg-gray-800'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }
                `}
              >
                <div className="flex items-center justify-center gap-2">
                  {status === 'IN_PROGRESS' && <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>}
                  {status === 'COMPLETED' && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
                  {STATUS_LABELS[status]}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Category Pills */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            {WORK_LOG_CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => {
                  onCategoryChange(category)
                  onStatusChange('all')
                }}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${selectedCategory === category
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-500 dark:to-primary-600 text-white shadow-md'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                  }
                `}
              >
                {category === 'all' ? '📋 전체' : category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">로딩 중...</p>
          </div>
        </div>
      ) : workLogs.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-500 dark:text-gray-300 text-lg">지원이력이 없습니다.</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">새 업무 기록을 추가해보세요.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {workLogs.map((workLog) => (
            <div
              key={workLog.id}
              onClick={() => onWorkLogClick(workLog)}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-primary-200 dark:hover:border-primary-400 transition-all duration-200 cursor-pointer group overflow-hidden"
            >
              <div className="p-6">
                {/* Header with badges */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span
                      className={`
                      inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
                      ${workLog.status === 'IN_PROGRESS'
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : 'bg-green-50 text-green-700 border border-green-200'
                        }
                    `}
                    >
                      {workLog.status === 'IN_PROGRESS' && (
                        <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></span>
                      )}
                      {workLog.status === 'COMPLETED' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {STATUS_LABELS[workLog.status]}
                    </span>
                    <span
                      className={`
                      px-3 py-1 rounded-full text-xs font-medium
                      ${getCategoryColor(workLog.category).replace('100', '50').replace('800', '700')}
                      border ${getCategoryColor(workLog.category).replace('bg-', 'border-').replace('100', '200')}
                    `}
                    >
                      {workLog.category}
                    </span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-300 transition-colors">
                  {workLog.title}
                </h3>

                {/* Customer name */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{workLog.customerName}</p>

                {/* Description preview (HTML 본문은 평문 스니펫으로 축약해 태그 노출 방지) */}
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">{toPlainPreview(workLog.description)}</p>

                {/* Time info */}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{formatDateTime(workLog.startedAt)}</span>
                  </div>
                  {workLog.durationSeconds && (
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="font-medium text-primary-600 dark:text-primary-400">{formatDuration(workLog.durationSeconds)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
